/***
*
*   FILE INPUT
*   File input 
*
*   DOCS
*   https://docs.useMapMetrics Atlas.app/MapMetrics Atlas-web/components/form
*   https://ui.shadcn.com/docs/components/input
*
*   PROPS
*   accept: accepted file types ['image/png', 'image/jpeg', 'image/jpg'] (array, optional)
*   aria-describedby: id of the element that describes the input in ...props (string, optional)
*   aria-invalid: determines if the input is invalid in ...props (boolean, required)
*   className: custom styling (SCSS or tailwind style, optional)
*   defaultValue: initial value (string, optional)
*   disabled: disable the input (boolean, optional)
*   name: input name (string, required)
*   preview: preview value (string, optional) 
*   placeholder: placeholder value (string, optional)
*   type: input type (string, required, default: text)
*   value: current value (string, optional)
*
**********/

import { forwardRef, useState, useEffect } from 'react';
import { cn, useTranslation, Image } from 'components/lib';

const FileInput = forwardRef(({ className, name, defaultValue, value, type, placeholder, onChange, disabled, accept, preview, ...props }, ref) => {

  const { t } = useTranslation();
  const [previewState, setPreviewState] = useState(preview);

  useEffect(() => {

    setPreviewState(preview);
    
  }, [preview]);

  function onFileChange(e){

    // validate filestypes
    if (accept?.length){
      for (let i = 0; i < e.target.files.length; i++) {

        const file = e.target.files[i];

        if (!accept.includes(file.type)){
          
          return onChange({ 

            type: 'error', 
            target: { name },
            errorMessage: t('global.form.file.error.excluded', { type: file.type.split('/')[1] })

          });
        }
      }
    }

    // update preview state
    const file = e.target?.files[0];
    const reader = new FileReader();

    reader.onloadend = () => {
      setPreviewState(reader.result);
    };

    reader.readAsDataURL(file);

    // do
    onChange({ 
      type: 'change',
      target: { 
        name: name, 
        value: e.target?.files
      }
    })
  }

  return (
    <div className={ cn('flex flex-col gap-4', previewState && 'border border-white/10 rounded-md p-4 items-center') }>

      { previewState && (previewState.includes('png') || previewState.includes('jpeg') || previewState.includes('jpg')) &&
        <Image className='max-h-32' src={ previewState } />
      }

      <input
        name={ name }
        ref={ ref }
        autoComplete={ name }
        disabled={ disabled }
        type={ type || 'text' }
        accept={ accept?.join(',') }
        placeholder={ placeholder }
        className={ cn('flex items-center h-10 w-full rounded-xl glass-input border-white/20 text-white px-3 py-2 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-white placeholder:text-white/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50', className, props['aria-invalid'] && 'border-red-500') }
        onChange={ onFileChange }
        {...props }
      />
    </div>
  )
})

FileInput.displayName = 'FileInput'
export { FileInput }